import { Type } from "@angular/core";
import { OnlyProGuard } from "./services/guards/only-pro.guard";
import { ProductCatalogAccessGuard } from "./services/guards/product-catalog-access.guard";
import { AccountResolverService } from "@services/resolvers/account-resolver.service";
import { TransactionalEmailsResolverService } from '@services/dws/resolvers/transactional-emails-resolver.service';
import { TemplateHtmlResolverService } from '@services/dws/resolvers/template-html-resolver.service';
import { TemplateHtmlsResolverService } from '@services/dws/resolvers/templates-html-resolver.service';
import { ContactsResolverService } from '@services/dws';
import { InactiveTransactionalEmailsResolverService } from '@services/dws/resolvers/inactive-transactional-emails-resolver-service';

export interface RouteConfigInstance {
  path: string;
  label?: string;
  showInMenu: boolean;
  showBreadcrumb?: boolean;
  /** When false, hides shell "Indietro" even if breadcrumb is on. Default: auto (breadcrumb + nested path). */
  showBackButton?: boolean;
  /**
   * Force stacked subheader layout (breadcrumb flow, not absolute) even when shell subtitle is cleared
   * because the page owns the title (e.g. ecommerce wizard).
   */
  stackedSubheader?: boolean;
  subtitle?: string;
  showSubtitle?: boolean;
  tooltip?: string;
  icon?: string;
  permission?: string;
  /** When set, route/menu checks use PermissionResolver (legacy + section.*.view/edit). */
  permissionSectionBase?: string;
  feature?: string;
  readPermission?: string[];
  writePermission?: string[];
  children?: RouteConfigInstance[];
  canLoad?: Type<any>[];
  proOnly?: boolean;
  resolve?: { [key: string]: Type<any> };
  pathMatch?: string;
  data?: any;

  redirectTo?: string;

  loadChildren?: () => Promise<any>;
}

const legacyProductsRouteRedirects: RouteConfigInstance[] = [
  {
    path: "products/:productId/orders/:orderId",
    redirectTo: "orders/:orderId",
    pathMatch: "full",
    showInMenu: false,
  },
  {
    path: "products/import",
    redirectTo: "product-catalog/products/import",
    pathMatch: "full",
    showInMenu: false,
  },
  {
    path: "products/copy",
    redirectTo: "product-catalog/products/new",
    pathMatch: "full",
    showInMenu: false,
  },
  {
    path: "products/choose-type",
    redirectTo: "product-catalog/products/choose-type",
    pathMatch: "full",
    showInMenu: false,
  },
  {
    path: "products/new",
    redirectTo: "product-catalog/products/new",
    pathMatch: "full",
    showInMenu: false,
  },
  {
    path: "products/:productId/edit",
    redirectTo: "product-catalog/products/:productId",
    pathMatch: "full",
    showInMenu: false,
  },
  {
    path: "products/:productId",
    redirectTo: "product-catalog/products/:productId",
    pathMatch: "full",
    showInMenu: false,
  },
  {
    path: "products",
    redirectTo: "product-catalog/products",
    pathMatch: "full",
    showInMenu: false,
  },
];

export const wsRoutes: RouteConfigInstance[] = [
  {
    path: "homepage",
    label: "Home Page",
    showInMenu: true,
    icon: "home",
    permissionSectionBase: "section.homepage",
    loadChildren: () => import('./pages/homepage/homepage.module').then(m => m.HomepageModule),
  },
  {
    path: "reports-dashboard",
    label: "Dashboard",
    showInMenu: true,
    loadChildren: () => import('./pages/reports-dashboard/reports-dashboard.module').then(m => m.ReportsDashboardModule),
    icon: "bar_chart_4_bars",
    permission: "DASHBOARD",
    children: [
      {
        path: "reservations",
        subtitle: "Reservations",
        showInMenu: true,
        icon: "event",
        permissionSectionBase: "section.dashboard.prenotazioni",
      },
      {
        path: "gifts",
        subtitle: "Gift",
        showInMenu: true,
        icon: "settings",
        permissionSectionBase: "section.dashboard.gift",
      },
      {
        path: "orders",
        subtitle: "Orders",
        showInMenu: true,
        icon: "shopping_cart",
        permissionSectionBase: "section.dashboard.ordini",
      },
      {
        path: "contacts",
        subtitle: "Contacts",
        showInMenu: true,
        icon: "person",
        permissionSectionBase: "section.dashboard.contatti",
      },
      {
        path: "marketing",
        subtitle: "Marketing",
        showInMenu: true,
        permissionSectionBase: "section.dashboard.marketing",
      },
    ],
  },
  {
    path: "calendar",
    label: "Calendar",
    showInMenu: true,
    subtitle: "",
    icon: "calendar_today",
    permissionSectionBase: "section.calendar",
    loadChildren: () => import('./pages/calendar/calendar.module').then(m => m.WSCalendarModule),
  },
  {
    path: "availability",
    label: "Availability management",
    showInMenu: true,
    icon: "schedule",
    permissionSectionBase: "section.availability",
    loadChildren: () => import('./pages/availability/availability.module').then(m => m.AvailabilityModule)
  },
  ...legacyProductsRouteRedirects,
  {
    path: "experiences",
    label: "Experiences",
    showInMenu: true,
    icon: "liquor",
    permissionSectionBase: "section.product_catalog.esperienze",
    loadChildren: () => import('./pages/experiences/experiences.module').then(m => m.ExperiencesModule),
    children: [
      {
        subtitle: "Experiences",
        path: "",
        showInMenu: true,
        showSubtitle: false,
      },
      {
        subtitle: "Additional questions",
        path: "additional-questions",
        showInMenu: true,
        showSubtitle: false,
        icon: "quiz",
      },
    ],
  },
  {
    path: "product-catalog",
    label: "Catalog",
    subtitle: "Products",
    showInMenu: true,
    icon: "inventory_2",
    permissionSectionBase: "section.product_catalog",
    proOnly: true,
    canLoad: [ProductCatalogAccessGuard],
    loadChildren: () => import('./pages/product-catalog/product-catalog.module').then(m => m.ProductCatalogModule),
    children: [
      {
        subtitle: "Products",
        path: "products",
        showInMenu: true,
        icon: "inventory",
        permissionSectionBase: "section.product_catalog.prodotti",
      },
      {
        subtitle: "Products Category",
        path: "categories",
        showInMenu: true,
        icon: "category",
        permissionSectionBase: "section.product_catalog.categorie",
      },
      {
        subtitle: "Attributes",
        path: "attributes",
        showInMenu: true,
        icon: "label",
        permissionSectionBase: "section.product_catalog.attributi",
      },
    ]},
  {
    path: "ecommerce",
    label: "Ecommerce",
    subtitle: "Ecommerce",
    showBreadcrumb: true,
    showBackButton: false,
    // Path only: no big blue «Ecommerce» title above the list.
    showSubtitle: false,
    stackedSubheader: true,
    // WS-4250: hide from sidebar for now (route still reachable by URL).
    showInMenu: false,
    icon: "storefront",
    permission: "section.ecommerce.shop.view",
    permissionSectionBase: "section.ecommerce.shop",
    proOnly: true,
    feature: "ecommerce",
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/ecommerce/ecommerce.module').then(m => m.EcommerceModule),
  },
  {
    path: "shipping",
    label: "shipping.title",
    subtitle: "shipping.title",
    showBreadcrumb: true,
    // Sidebar sub-pages (to-ship, zones): path only, no «Indietro».
    showBackButton: false,
    stackedSubheader: true,
    showInMenu: true,
    icon: "local_shipping",
    permission: "section.shipping.da_seguire.view",
    permissionSectionBase: "section.shipping.da_seguire",
    proOnly: true,
    feature: "ecommerce",
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/shipping/shipping.module').then(m => m.ShippingModule),
    // N-27 / T-12: sidebar children (no in-page tabs).
    children: [
      {
        subtitle: "Ordini da spedire",
        path: "to-ship",
        showInMenu: true,
        icon: "local_shipping",
      },
      {
        subtitle: "Zone e tariffe",
        path: "zones",
        showInMenu: true,
        icon: "map",
      },
    ],
  },
  {
    path: "pos",
    label: "Point of sale",
    showInMenu: true,
    icon: "point_of_sale",
    permissionSectionBase: "section.point_of_sale.general",
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/pos/pos.module').then(m => m.POSModule),
  },
  {
    path: "pos-terminal",
    label: "POS WS BETA",
    subtitle: "POS Wine Suite BETA",
    showInMenu: false,
    icon: "point_of_sale",
    permissionSectionBase: "section.point_of_sale.general",
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/pos-terminal/pos-terminal.module').then(m => m.POSterminalModule),
  },
  {
    path: "",
    label: "Sales",
    showInMenu: true,
    icon: "shopping_bag",
    children: [
      {
        path: "reservations",
        showInMenu: true,
        subtitle: "Reservations",
        icon: "event",
        permissionSectionBase: "section.sales.prenotazioni",
        loadChildren: () => import('./pages/reservations/reservations.module').then(m => m.ReservationsModule),
      },
      {
        subtitle: "Gift",
        path: "gifts",
        showInMenu: true,
        icon: "settings",
        permissionSectionBase: "section.sales.gift",
        loadChildren: () => import('./pages/gifts/gifts.module').then(m => m.GiftsModule),
      },
      {
        subtitle: "Orders",
        path: "orders",
        showInMenu: true,
        icon: "shopping_cart",
        permissionSectionBase: "section.sales.ordini",
        canLoad: [OnlyProGuard],
        loadChildren: () => import('./pages/orders/orders.module').then(m => m.OrdersModule),
      },
      {
        subtitle: "Self Order",
        path: "preorders",
        showInMenu: true,
        icon: "event",
        permissionSectionBase: "section.sales.self_order",
        loadChildren: () => import('./pages/preorders/preorders.module').then(m => m.PreordersModule),
      },
      {
        subtitle: "Discount Code",
        path: "discount",
        showInMenu: true,
        icon: "discount",
        permissionSectionBase: "section.sales.codici_sconto",
        canLoad: [],
        loadChildren: () => import('projects/web/src/app/pages/discount-code/discount-code.module').then(m => m.DiscountCodeModule),
      },
    ],
  },
  {
    path: "fiscality",
    label: "WineSuite Fiscality",
    subtitle: "Fiscal Documents",
    showInMenu: true,
    icon: "account_balance",
    permissionSectionBase: "section.ws_fiscality.general",
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/receipts/receipts.module').then(m => m.ReceiptsModule),
  },
  {
    path: "payments",
    label: "WineSuite Payments",
    subtitle: "",
    showInMenu: true,
    icon: "payment",
    permissionSectionBase: "section.ws_payments.general",
    loadChildren: () => import('./pages/payments/payments.module').then(m => m.PaymentsModule),
  },
  {
    path: "",
    label: "CRM",
    showInMenu: true,
    icon: "contacts",
    permission: "CRM",
    canLoad: [OnlyProGuard],
    children: [
      {
        subtitle: "Contacts",
        path: "contacts",
        showInMenu: true,
        icon: "person",
        permissionSectionBase: "section.crm.contatti",
        canLoad: [OnlyProGuard],
        loadChildren: () => import('./pages/contacts/contacts.module').then(m => m.ContactsModule),
      },
      {
        subtitle: "Tags",
        path: "tags",
        showInMenu: true,
        icon: "label",
        permissionSectionBase: "section.crm.tag",
        canLoad: [OnlyProGuard],
        loadChildren: () => import('./pages/tags/tags.module').then(m => m.TagsModule),
      },
      {
        subtitle: "Segments",
        path: "segments",
        showInMenu: true,
        icon: "segment",
        permissionSectionBase: "section.crm.segmenti",
        canLoad: [OnlyProGuard],
        loadChildren: () => import('./pages/segments/segments.module').then(m => m.SegmentsModule),
      },
      {
        subtitle: "Import",
        path: "winery-contact-import",
        showInMenu: false,
        icon: "import",
        readPermission: ["section.crm.contatti.edit"],
        canLoad: [OnlyProGuard],
        loadChildren: () => import('./pages/winery-contact-import/winery-contact-import-module').then(m => m.WineryContactImportModule),
      },
      // {
      //   subtitle: "Customer Classes",
      //   path: "customer-classes",
      //   showInMenu: false,
      //   icon: "class",
      //   canLoad: [OnlyProGuard],
      //   loadChildren: () => import('./pages/customer-classes/customer-classes.module').then(m => m.CustomerClassesModule),
      // },
    ],
  },
  {
    path: "marketing",
    label: "Marketing",
    showInMenu: true,
    icon: "campaign",
    permission: "MARKETING",
    permissionSectionBase: "section.marketing",
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/marketing/marketing.module').then(m => m.MarketingModule),
    children: [
      {
        subtitle: "Campaigns",
        path: "campaigns",
        showInMenu: true,
      },
      {
        subtitle: "Automations",
        path: "automations",
        showInMenu: true,
        icon: "automation",
      },
      {
        subtitle: "Marketing lists",
        path: "lists",
        showInMenu: true,
        icon: "list",
      },
      {
        subtitle: "Form",
        path: "form",
        showInMenu: true,
        icon: "form",
      },
    ],
  },
  {
    label: "Integrations",
    showInMenu: true,
    icon: "code",
    path: "integrations",
    permissionSectionBase: "section.integrations",
    resolve: {
      login: AccountResolverService
    },
    loadChildren: () => import('projects/web/src/app/pages/integrations/integrations.module').then(m => m.IntegrationsModule),
    showBreadcrumb: true,
  },
  {
    path: "settings",
    label: "Settings",
    showInMenu: true,
    icon: "settings",
    permissionSectionBase: "section.settings",
    children: [
      {
        subtitle: "Account Management",
        path: "company-management",
        showInMenu: true,
        icon: "account_circle",
        permissionSectionBase: "section.settings.gestione_account",
        loadChildren: () => import('projects/web/src/app/pages/company-management/company-management.module').then(m => m.CompanyManagementModule),
      },
      {
        subtitle: "Transactional Emails",
        path: "transactional-emails",
        showInMenu: true,
        icon: "email",
        permissionSectionBase: "section.settings.email_transazionali",
        loadChildren: () => import('projects/web/src/app/pages/transactional-emails/transactional-emails.module').then(m => m.TransactionalEmailsModule),
        resolve: {
          transactionalEmails: TransactionalEmailsResolverService,
          templatesHTMLs: TemplateHtmlsResolverService,
          login: AccountResolverService,
          alreadyInactiveTrigger: InactiveTransactionalEmailsResolverService
        },
        data: { admin: false },
      },
      {
        subtitle: "Template HTMLs",
        path: "template-htmls/:templateHtmlType",
        showInMenu: false,
        permissionSectionBase: "section.settings.email_transazionali",
        loadChildren: () => import('projects/web/src/app/pages/template-html-detail/template-html-detail-routing.module')
          .then(m => m.TemplateHtmlDetailRoutingModule),
        data: { admin: false },
        resolve: {
          templatesHTMLs: TemplateHtmlsResolverService,
          templatesHTML: TemplateHtmlResolverService,
          login: AccountResolverService
        },
      },
      {
        subtitle: "Developers",
        path: "developers",
        showInMenu: true,
        icon: "developer_mode",
        permissionSectionBase: "section.settings.sviluppatori",
        canLoad: [OnlyProGuard],
        loadChildren: () => import('projects/web/src/app/pages/developers/developers.module').then(m => m.DevelopersModule),
        resolve: {
          login: AccountResolverService
        },
      },
      {
        subtitle: "wAIne Agent (Beta)",
        path: "ai-agent",
        showInMenu: false,
        icon: "smart_toy",
        permission: "AI_AGENT_USE",
        canLoad: [OnlyProGuard],
        loadChildren: () =>
          import("projects/web/src/app/pages/ai-agent/ai-agent.module").then(
            (m) => m.AiAgentModule
          ),
        resolve: {
          login: AccountResolverService,
        },
      },
      {
        subtitle: "Point of Sale Wine Suite",
        path: "point-of-sale-wine-suite",
        tooltip: "tooltip.point_of_sale_wine_suite",
        showInMenu: true,
        icon: "point_of_sale",
        permissionSectionBase: "section.settings.punto_cassa",
        canLoad: [OnlyProGuard],
        loadChildren: () => import('./pages/point-of-sale-wine-suite/point-of-sale-wine-suite.module').then(m => m.PointOfSaleWineSuiteModule),
      },
      {
        subtitle: "Utenti e Ruoli",
        path: "users-and-roles",
        showInMenu: true,
        icon: "people",
        permissionSectionBase: "section.settings.utenti_ruoli",
        loadChildren: () => import('./pages/users-and-roles/users-and-roles.module').then(m => m.UsersAndRolesModule)
      },
    ],
  },
  {
    path: "winesuite-jobs-async",
    loadChildren: () =>
      import("./pages/winesuite-jobs-async-window/winesuite-jobs-async-window.module").then(
        (m) => m.WineSuiteJobsAsyncWindowModule
      ),
    showInMenu: false,
  },
  {
    path: 'login',
    showInMenu: false,
    loadChildren: () => import('./pages/login/login.module').then(m => m.LoginModule),
  },
  {
    path: 'super-admin-grant',
    showInMenu: false,
    loadChildren: () => import('./pages/super-admin-grant/super-admin-grant.module').then(m => m.SuperAdminGrantModule),
  },
  {
    path: '',
    redirectTo: 'homepage',
    pathMatch: 'full',
    showInMenu: false,
  },
  {
    path: 'homepage',
    loadChildren: () => import('./pages/homepage/homepage.module').then(m => m.HomepageModule),
    showInMenu: false,
    permissionSectionBase: "section.homepage",
  },
  {
    path: 'homepage-old',
    loadChildren: () => import('./pages/homepage-old/homepage.module-old').then(m => m.HomepageOldModule),
    showInMenu: false,
  },
  {
    path: 'experiences/:experienceId',
    //loadChildren: () => import('./pages/experience-detail/experience-detail.module').then(m => m.ExperienceDetailModule),
    loadChildren: () => import('./pages/experience-details/experience-details.module').then(m => m.ExperienceDetailsModule),
    showInMenu: false,
    permissionSectionBase: "section.product_catalog.esperienze",
  },
  {
    path: 'experiences_v2/:experienceId',
    loadChildren: () => import('./pages/experience-detail/experience-detail.module').then(m => m.ExperienceDetailModule),
    //loadChildren: () => import('./pages/experience-details/experience-details.module').then(m => m.ExperienceDetailsModule),
    showInMenu: false,
    permissionSectionBase: "section.product_catalog.esperienze",
  },
  {
    path: 'discount/:discountCodeId',
    canLoad: [],
    loadChildren: () => import('projects/web/src/app/pages/discount-code/new-discount-code/new-discount-code.module').then(m => m.NewDiscountCodeModule),
    showInMenu: false,
  },
  {
    path: 'contacts/:contactId/:companyId',
    permissionSectionBase: 'section.crm.contatti',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/contact-detail/contact-detail.module').then(m => m.ContactDetailModule),
    showInMenu: false,
  },
  {
    path: 'tags/new',
    permissionSectionBase: 'section.crm.tag',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/tags/new-tag/new-tag.module').then(m => m.NewTagModule),
    showInMenu: false,
  },
  {
    path: 'segments/new',
    permissionSectionBase: 'section.crm.segmenti',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/segments/new-segment/new-segment.module').then(m => m.NewSegmentModule),
    showInMenu: false,
  },
  {
    path: 'segments/:segmentId/edit',
    permissionSectionBase: 'section.crm.segmenti',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/segments/new-segment/new-segment.module').then(m => m.NewSegmentModule),
    showInMenu: false,
  },
  {
    path: 'tags/new/:tagId',
    permissionSectionBase: 'section.crm.tag',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/tags/new-tag/new-tag.module').then(m => m.NewTagModule),
    showInMenu: false,
  },
  {
    path: 'account-detail',
    subtitle: "Account Detail",
    loadChildren: () => import('./pages/account-detail/account-detail.module').then(m => m.AccountDetailModule),
    showInMenu: false,
  },
  {
    path: 'orders/import',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/orders-import/orders-import.module').then(m => m.OrdersImportModule),
    showInMenu: false,
  },
  {
    path: 'orders/:orderId',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/order-detail/order-detail.module').then(m => m.OrderDetailModule),
    showInMenu: false,
  },
  {
    path: 'customer-classes/new',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/customer-class-detail/customer-class-detail.module').then(m => m.CustomerClassDetailModule),
    showInMenu: false,
  },
  {
    path: 'customer-classes/:id',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/customer-class-detail/customer-class-detail.module').then(m => m.CustomerClassDetailModule),
    showInMenu: false,
  },
  {
    path: 'product-categories/new',
    loadChildren: () => import('./pages/product-categories/new-product-category/new-product-category.module').then(m => m.NewProductCategoryModule),
    showInMenu: false,
  },
  {
    path: 'product-categories/:id',
    loadChildren: () => import('./pages/product-categories/new-product-category/new-product-category.module').then(m => m.NewProductCategoryModule),
    showInMenu: false,
  },
  {
    path: 'pos/:orderId',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/pos/pos.module').then(m => m.POSModule),
    showInMenu: false,
  },
  {
    path: 'pos/self-order/:preorderId',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/pos/pos.module').then(m => m.POSModule),
    showInMenu: false,
  },
  {
    path: 'pos-terminal/:orderId',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/pos/pos.module').then(m => m.POSModule),
    showInMenu: false,
  },
  {
    path: 'pos-terminal/self-order/:preorderId',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('./pages/pos/pos.module').then(m => m.POSModule),
    showInMenu: false,
  },
  {
    path: 'registration/:companyId',
    loadChildren: () => import('./pages/registration/registration.module').then(m => m.RegistrationModule),
    showInMenu: false,
  },
  {
    path: 'privacy',
    loadChildren: () => import('./pages/privacy/privacy.module').then(m => m.PrivacyModule),
    showInMenu: false,
  },
  {
    path: 'registration/:companyId/:contactId',
    loadChildren: () => import('./pages/registration/registration.module').then(m => m.RegistrationModule),
    resolve: {
      contact: ContactsResolverService
    },
    showInMenu: false,
  },
  {
    path: 'registration/:companyId/qr-code/:qrcodeId',
    loadChildren: () => import('./pages/registration/registration.module').then(m => m.RegistrationModule),
    showInMenu: false,
  },
  {
    path: 'qr-code/:companyId/:qrcodeId',
    loadChildren: () => import('./pages/qr-code/qr-code.module').then(m => m.QrCodeModule),
    showInMenu: false,
  },
  {
    path: 'unsubscribe/:companyId/:contactId/:contactEmail',
    loadChildren: () => import('./pages/unsubscribe/unsubscribe.module').then(m => m.UnsubscribeModule),
    showInMenu: false,
  },
  {
    path: 'unsubscribe/:companyId/:contactId/:contactEmail/:campaignId',
    loadChildren: () => import('./pages/unsubscribe/unsubscribe.module').then(m => m.UnsubscribeModule),
    showInMenu: false,
  },
  {
    path: 'registration-code',
    loadChildren: () => import('./pages/registration-code/registration-code.module').then(m => m.RegistrationCodeModule),
    showInMenu: false,
  },
  {
    path: 'preorders-code',
    loadChildren: () => import('./pages/preorders-code/preorders-code.module').then(m => m.PreordersCodeModule),
    showInMenu: false,
  },
  {
    path: 'admin',
    loadChildren: () => import('./pages/admin/admin.module').then(m => m.AdminModule),
    showInMenu: false,
  },
  {
    path: 'reset-password/:token',
    loadChildren: () => import('projects/web/src/app/pages/reset-password/reset-password.module').then(m => m.ResetPasswordModule),
    showInMenu: false,
  },
  {
    path: 'set-password/:token',
    loadChildren: () => import('projects/web/src/app/pages/set-password/set-password.module').then(m => m.SetPasswordModule),
    showInMenu: false,
  },
  {
    path: 'mfa-reenroll/:token',
    loadChildren: () => import('projects/web/src/app/pages/mfa-reenroll/mfa-reenroll.module').then(m => m.MfaReenrollModule),
    showInMenu: false,
  },
  /*{
    path: 'integrations/stripe-config',
    canLoad: [OnlyProGuard],
    loadChildren: () => import('@web/app/pages/integrations/integration-config/stripe-config/stripe-config.module').then(m => m.StripeConfigModule),
    resolve: {
      login: AccountResolverService
    },
    showInMenu: false,
  },*/
];